import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Spinner } from 'reactstrap';
import { Link } from 'react-router-dom';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import Thread from './CommsThread';
import { CHAT_PATH, chat_row_title, clear_needs_human, client_chats_path, needs_human_since, order_conversations, owner_verified } from '../../Chat/user_chat';
import { AttendedMark, KIND_CHIP, KindIcon, VerifiedTick, WaitBadge } from '../../Chat/row_bits';
import FaIcon from '../../../components/FaIcon';

/**
 * Client page → Chats (CT-15539): this org's users' private portal chats — each
 * user's one thread with the Infurnia Assistant first and the CS team behind
 * it — listed apart from the org's WhatsApp groups (those stay on the Comms
 * tab). Rows read exactly as in the Chat inbox (same helpers, same bits);
 * waiting-for-a-person threads lead, oldest ask first. One Thread mounts at a
 * time (mounting advances the read cursor).
 *
 * Source: GET /api/v1/comms/clients/:storeId/chats → { conversations } in the
 * feed's row shape, under the feed's access rule. The route ships with the api
 * separately: until then (or on any failure) the tab shows a quiet empty
 * state, never an error page.
 */

const EMPTY_TEXT = 'Private chats are not available yet';

const fmt_ts = (d) => {
    try {
        const t = new Date(d); const now = new Date();
        return t.toDateString() === now.toDateString()
            ? t.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
            : t.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    } catch (e) { return ''; }
};

/** What the tab shows for a response: the rows, or the empty state on any shape it does not know. */
export const chats_of = (resp) => (resp && Array.isArray(resp.conversations) ? resp.conversations.filter(Boolean) : null);

export default function ClientChats({ store_id }) {
    const [rows, set_rows] = useState(null); // null = loading; [] = none / unavailable
    const [unavailable, set_unavailable] = useState(false);
    const [selected_id, set_selected_id] = useState(null);
    const [me_id, set_me_id] = useState(null);

    // Loads are serialised and generation-stamped: the 30 s poll never starts a second load while
    // one is in flight, and a response is applied only if it is still the current generation — a
    // slow older response can neither overwrite newer rows nor repopulate chats after the
    // unavailable state (Copilot on #201). A store change bumps the generation so the previous
    // org's response is dropped too.
    const generation = useRef(0);
    const in_flight = useRef(false);
    useEffect(() => { generation.current += 1; in_flight.current = false; set_rows(null); set_unavailable(false); set_selected_id(null); }, [store_id]);
    const load = useCallback(async () => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        if (in_flight.current) return;
        const gen = generation.current;
        in_flight.current = true;
        try {
            const resp = await cs_analysis_fetch({ path: client_chats_path(store_id) });
            if (gen !== generation.current) return;
            const list = chats_of(resp);
            // A 2xx with an unexpected shape is the unavailable state too: the selection goes with the
            // rows, so a later good poll cannot reopen the old thread without a click (Copilot on #201).
            if (list === null) { set_unavailable(true); set_rows([]); set_selected_id(null); return; }
            set_rows(list);
            set_unavailable(false);
        } catch (e) {
            if (gen !== generation.current) return;
            // 404 until the api ships the route, the org outside the rep's access, or a poll that
            // failed after a good load: the unavailable state replaces the rows — never stale chats.
            set_unavailable(true);
            set_rows([]);
            set_selected_id(null);
        } finally {
            if (gen === generation.current) in_flight.current = false;
        }
    }, [store_id]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
    useEffect(() => {
        cs_analysis_fetch({ path: '/api/v1/auth/me' }).then((r) => set_me_id(r && r.user && r.user.id)).catch(() => {});
    }, []);

    const list = order_conversations(rows || []);
    const selected = list.find((c) => c.channelId === selected_id) || null;
    const open = (c) => {
        set_selected_id(c.channelId);
        // Optimistic: the mounted Thread advances the server cursor.
        set_rows((cur) => (cur || []).map((x) => (x.channelId === c.channelId ? { ...x, unread: 0 } : x)));
    };

    return (
        <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', minHeight: 420 }}>
            <div style={{ flex: '0 0 40%', minWidth: 320, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                    Private chats {rows && rows.length ? `(${rows.length})` : ''}
                    <span style={{ fontSize: 11, color: '#95a5a6', fontWeight: 400, marginLeft: 8 }}>
                        one per portal user · assistant first, CS behind
                    </span>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e8ecef', borderRadius: 8, background: '#fff', maxHeight: 'calc(100vh - 260px)' }}>
                    {rows === null ? <div style={{ padding: 16 }}><Spinner size='sm' /> loading…</div> : null}
                    {rows !== null && list.length === 0 ? (
                        <div style={{ padding: 16, fontSize: 12, color: '#95a5a6' }} data-testid='client-chats-empty'>
                            {unavailable ? EMPTY_TEXT : 'No portal user of this org has written to the assistant yet.'}
                        </div>
                    ) : null}
                    {list.map((c) => (
                        // A row is a button for the keyboard too (Enter / Space open it, Tab reaches it).
                        <div key={c.channelId} onClick={() => open(c)} data-testid='client-chat-row'
                            role='button' tabIndex={0} aria-pressed={selected_id === c.channelId}
                            aria-label={`Open chat with ${chat_row_title(c)}`}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(c); } }}
                            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f3f5',
                                background: selected_id === c.channelId ? '#eef4ff' : c.unread ? '#fbfdff' : '#fff' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <b style={{ fontSize: 13, color: '#2c3e50', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                                    <KindIcon c={c} />{chat_row_title(c)}{owner_verified(c) ? <VerifiedTick /> : null}
                                </b>
                                <span style={{ fontSize: 11, color: '#95a5a6', whiteSpace: 'nowrap' }}>{fmt_ts(c.lastMessageAt)}</span>
                            </div>
                            <div style={{ fontSize: 11, color: '#7f8c8d', margin: '1px 0', display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                                <span style={KIND_CHIP}>Chat</span>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>private · assistant + CS</span>
                                {c.needsHumanAt ? <span style={{ marginLeft: 'auto' }}><WaitBadge at={c.needsHumanAt} /></span>
                                    : <span style={{ marginLeft: 'auto' }}><AttendedMark c={c} /></span>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 12, color: '#5d6d7e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 250 }}>
                                    {c.lastAuthorName ? `${c.lastAuthorName}: ` : ''}{c.lastBody || ''}
                                </span>
                                {c.unread ? (
                                    c.participating
                                        ? <Badge color='primary' pill style={{ fontSize: 10 }}>{c.unread > 99 ? '99+' : c.unread}</Badge>
                                        : <span style={{ fontSize: 9.5, color: '#2f6fed', border: '1px solid #cfe0ff', borderRadius: 9, padding: '1px 7px', background: '#f4f8ff' }}>new</span>
                                ) : null}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #e8ecef', borderRadius: 8, background: '#fff', padding: 12, minHeight: 420 }}>
                {!selected ? (
                    <div style={{ margin: 'auto', color: '#95a5a6', fontSize: 13 }}>Select a chat to read and reply.</div>
                ) : (
                    <>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8, borderBottom: '1px solid #f1f3f5', paddingBottom: 8 }}>
                            <b style={{ fontSize: 14 }}><KindIcon c={selected} size={13} />{chat_row_title(selected)}{owner_verified(selected) ? <VerifiedTick size={12} /> : null}</b>
                            <span style={{ fontSize: 12, color: '#7f8c8d' }}><span style={KIND_CHIP}>Chat</span> · {selected.clientName}</span>
                            {selected.needsHumanAt ? (
                                <span title='The client asked to talk to a person — your reply clears this'
                                    style={{ fontSize: 11, color: '#8a6d1d', background: '#fff8e6', border: '1px solid #f0d48a', borderRadius: 10, padding: '1px 9px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    <FaIcon name='hand-paper' label='needs a person' /> Needs a person since {needs_human_since(selected.needsHumanAt)}
                                </span>
                            ) : null}
                            <span style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'baseline' }}>
                                <Link to={`${CHAT_PATH}?channel=${encodeURIComponent(selected.channelId)}`} style={{ fontSize: 12 }} title='Open in the Chat inbox (search, media, tickets)'>
                                    open in Chat →
                                </Link>
                                <button type='button' onClick={() => set_selected_id(null)} title='Close chat' aria-label='Close chat' data-testid='client-chat-close'
                                    style={{ fontSize: 15, color: '#95a5a6', cursor: 'pointer', lineHeight: 1, padding: '0 2px', background: 'none', border: 'none', font: 'inherit' }}><FaIcon name='times' /></button>
                            </span>
                        </div>
                        <div style={{ flex: 1, minHeight: 0 }}>
                            <Thread key={selected.channelId} channel_id={selected.channelId} allow_internal={false} private_chat height={480}
                                placeholder={`Message ${chat_row_title(selected)}…`} store_id={store_id} me_id={me_id}
                                on_sent={() => {
                                    // A staff post clears needsHumanAt server-side; reflect it at once (the poll confirms within 30s).
                                    if (!selected.needsHumanAt) return;
                                    set_rows((cur) => clear_needs_human(cur, selected.channelId));
                                }} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
